import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Thread } from './chat/Thread';
import { useCustomLangGraphRuntime } from '../services/langGraphRuntime';
import { shortid } from '../services/id.service';
import { ConversationHistory } from './chat/ConversationHistory';
import { AdvancedThreadControls, useAdvancedThreadControls } from './chat/AdvancedThreadControls';
import { ThreadSettingsContext } from './chat/threadSettings';
import { useQuery } from '@tanstack/react-query';
import { useTenantConfig } from '../hooks/useTenantConfig';
import { getPlatformDefaultModel } from '../services/data.store';

// Used only until the per-tenant platform default (GET /models/default) loads,
// or if that request fails. Sonnet 5 is served everywhere Sonnet 5.5 is not.
const FALLBACK_ELLIE_MODEL = 'claude-sonnet-5';

interface EllieProps {
  initialQuestion?: string;
  showHistory?: boolean;
  conversationId?: string;
  agentType?: 'analyst' | 'use_case_discovery' | 'eda' | 'data_modeling' | 'ellie';
  sourceId?: string;
  dataSourceId?: string;
  useCaseId?: string;
  onValidationComplete?: () => void;
  // Embedded (iframe) chat: settings come pinned from the embed session's
  // thread, the advanced-controls panel is unavailable, and verbosity
  // defaults to low while those settings load.
  embedded?: boolean;
  // Center the input on a brand-new conversation. When omitted, falls back to
  // the tenant default (Settings → Ellie). Embeds pass the value pinned on
  // their session JWT so the tenant default doesn't leak into the iframe.
  centerInputOnNewChat?: boolean;
  // Embed-configurable presentation (from the embed session's render config).
  // When omitted the chat uses its in-app defaults.
  placeholder?: string;
  welcomeMessage?: string;
}

// Wrapper component that renders the assistant with a specific thread ID
const AssistantWrapper = ({
  threadId,
  initialQuestion,
  onAnalysisGenerated,
  agentType = 'analyst',
  defaultModel,
  sourceId,
  dataSourceId,
  useCaseId,
  onValidationComplete,
  embedded = false,
  centerInputOnNewChat = false,
  placeholder,
  welcomeMessage,
}: {
  threadId: string;
  initialQuestion: string;
  onAnalysisGenerated: (analysisId: string) => void;
  agentType?: 'analyst' | 'use_case_discovery' | 'eda' | 'data_modeling' | 'ellie';
  defaultModel: string;
  sourceId?: string;
  dataSourceId?: string;
  useCaseId?: string;
  onValidationComplete?: () => void;
  embedded?: boolean;
  centerInputOnNewChat?: boolean;
  placeholder?: string;
  welcomeMessage?: string;
}) => {
  // Verbosity control + advanced controls only apply to the Ellie chat
  // surfaces, not to the guided flows (EDA, use case discovery, data modeling)
  // that reuse this wrapper.
  const verbosityCapable = agentType === 'ellie' || agentType === 'analyst';

  const {
    controlsEnabled,
    showControls,
    settings,
    applyLoadedSettings,
    handleSettingsChange,
  } = useAdvancedThreadControls({
    threadId,
    defaultModel,
    // Defaulting to high (current behavior) for the initial release; we may
    // move the default to low (terse + hidden tool activity) later.
    // Embeds are low out of the box, matching the chat_config default —
    // the thread's pinned settings overwrite this once loaded.
    defaultVerbosity: embedded ? 'low' : 'high',
    supported: verbosityCapable,
    // The settings plumbing stays active (the pinned thread settings drive
    // verbosity/concise mode), but the panel + hotkey are never available.
    controlsHidden: embedded,
  });

  const { runtime, isHistoricalThread, isLoaded, accessDenied, resumeGraph, compactConversation, messageCount, threadSettings } = useCustomLangGraphRuntime({
    agentType,
    model: settings.model,
    verbosity: verbosityCapable ? settings.verbosity : undefined,
    reasoning: verbosityCapable ? settings.reasoning : undefined,
    threadId,
    sourceId,
    dataSourceId,
    useCaseId,
  });

  useEffect(() => {
    applyLoadedSettings(threadSettings);
  }, [threadSettings, applyLoadedSettings]);

  // Auto-run charts for new threads only
  // Historical threads should start with charts collapsed to avoid loading all charts at once
  const autoRunEnabled = !isHistoricalThread;

  // Wait until the thread's messages have loaded before rendering the chat when
  // centering is on. Otherwise an empty thread briefly shows the centered
  // new-chat layout before its history arrives, then the input jumps to the
  // bottom — a visible flash, most noticeable when switching threads from the
  // history sidebar. (AssistantWrapper is keyed on threadId, so isLoaded resets
  // per switch.) When centering is off the input is always at the bottom, so
  // there's nothing to flash and we render immediately as before.
  if (!isLoaded && (centerInputOnNewChat || isHistoricalThread)) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center max-w-sm">
          <p className="font-medium text-foreground">You don't have permission to view this conversation</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {controlsEnabled && (
        <AdvancedThreadControls
          open={showControls}
          settings={settings}
          onSettingsChange={handleSettingsChange}
          threadId={threadId}
        />
      )}
      <div className="flex-1 min-h-0">
        <ThreadSettingsContext.Provider value={verbosityCapable ? settings : undefined}>
          <Thread
            runtime={runtime}
            initialQuestion={initialQuestion}
            toolCallHandlers={{
              onAnalysisGenerated,
              // Lets interrupt-based tools (ask_user_choice) resume the graph
              onResumeGraph: resumeGraph,
            }}
            autoRunCharts={autoRunEnabled}
            onValidationComplete={onValidationComplete}
            onCompact={compactConversation}
            messageCount={messageCount}
            centerInputOnNewChat={centerInputOnNewChat}
            placeholder={placeholder}
            emptyStateMessage={welcomeMessage}
          />
        </ThreadSettingsContext.Provider>
      </div>
    </div>
  );
};

export const Ellie: React.FC<EllieProps> = ({
  initialQuestion = '',
  showHistory = false,
  conversationId,
  agentType = 'analyst',
  sourceId,
  dataSourceId,
  useCaseId,
  onValidationComplete,
  embedded = false,
  centerInputOnNewChat,
  placeholder,
  welcomeMessage,
}) => {
  // Use conversationId if provided, otherwise generate a new thread ID.
  // In the embedded iframe we can't navigate the router (the URL stays on
  // /embed), so switching/creating conversations is tracked in local state
  // instead. In-app keeps using router navigation.
  const [activeThreadId, setActiveThreadId] = useState(() => conversationId || shortid());
  const currentThreadId = embedded ? activeThreadId : (conversationId || activeThreadId);
  const navigate = useNavigate();

  // New chats default to the tenant-configured model (Settings → Ellie),
  // falling back to the platform default. Embeds ignore this — their model is
  // pinned server-side by the embed session.
  const { data: tenantConfig } = useTenantConfig();
  const { data: platformDefaultModel } = useQuery({
    queryKey: ['platformDefaultModel'],
    queryFn: getPlatformDefaultModel,
    enabled: !embedded,
    staleTime: 30 * 60 * 1000,
  });
  const defaultModel =
    (tenantConfig?.ellie_model as string | undefined) || platformDefaultModel?.id || FALLBACK_ELLIE_MODEL;

  // Embeds pass this in as a prop; in-app falls back to the tenant default,
  // which itself defaults off (preserving the original bottom-docked layout).
  const resolvedCenterInput =
    centerInputOnNewChat ??
    ((tenantConfig?.ellie_center_input_on_new_chat as boolean | undefined) ?? false);

  // Keep local state in sync when the route-driven conversationId changes
  // (in-app history navigation). Embedded mode owns the id locally.
  useEffect(() => {
    if (!embedded && conversationId) {
      setActiveThreadId(conversationId);
    }
  }, [conversationId, embedded]);

  const [analysisIds, setAnalysisIds] = useState<string[]>([]);

  const handleNewChat = useCallback(() => {
    if (embedded) {
      setActiveThreadId(shortid());
    } else {
      navigate(`/ellie/conversation/${shortid()}`);
    }
  }, [navigate, embedded]);

  const handleSelectThread = useCallback((threadId: string) => {
    setActiveThreadId(threadId);
  }, []);

  const content = (
    <AssistantWrapper
      key={`${currentThreadId}:${defaultModel}`}
      threadId={currentThreadId}
      initialQuestion={initialQuestion}
      agentType={agentType}
      defaultModel={defaultModel}
      sourceId={sourceId}
      dataSourceId={dataSourceId}
      useCaseId={useCaseId}
      onValidationComplete={onValidationComplete}
      embedded={embedded}
      centerInputOnNewChat={resolvedCenterInput}
      placeholder={placeholder}
      welcomeMessage={welcomeMessage}
      onAnalysisGenerated={(analysisId) => {
        if (analysisId && !analysisIds.includes(analysisId)) {
          setAnalysisIds([...analysisIds, analysisId]);
        }
      }}
    />
  );

  if (showHistory) {
    return (
      <div className="h-full w-full flex">
        <div className="flex-[3] min-w-0">
          {content}
        </div>
        <div className="flex-1 min-w-0">
          <ConversationHistory
            activeThreadId={currentThreadId}
            onNewChat={handleNewChat}
            onSelectThread={embedded ? handleSelectThread : undefined}
            agentType={agentType}
            agentTypes={agentType === 'ellie' ? ['ellie', 'analyst'] : undefined}
            // In-app admins can widen the list to the whole tenant. Never in an
            // embed: that history belongs to one end-user, and the embed token
            // isn't an Admin anyway.
            allowAllChats={!embedded}
            className="h-full border-l bg-background"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full">
      {content}
    </div>
  );
};
